import { GlModal, GlSearchBoxByType, GlButton, GlIcon, GlCollapse, GlEmptyState, GlLoadingIcon } from '@gitlab/ui';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { s__, n__, sprintf } from '~/locale';
import axios from '~/lib/utils/axios_utils';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import { HTTP_STATUS_TOO_MANY_REQUESTS } from '~/lib/utils/http_status';
import { onboardingFeatureLibrarySearchPath, onboardingFeatureLibraryAiSearchPath } from '~/lib/utils/path_helpers/feature_library';
import { InternalEvents } from '~/tracking';
import ScrollScrim from '~/vue_shared/components/scroll_scrim/scroll_scrim.vue';
import { EVENT_OPEN_FEATURE_LIBRARY_MODAL, EVENT_SEARCH_FEATURES_IN_FEATURE_LIBRARY_MODAL, EVENT_CLICK_CATEGORY_FILTER_IN_FEATURE_LIBRARY_MODAL, EVENT_PIN_ITEM_IN_FEATURE_LIBRARY_MODAL, EVENT_UNPIN_ITEM_IN_FEATURE_LIBRARY_MODAL, EVENT_NAVIGATE_TO_FEATURE_FROM_FEATURE_LIBRARY_MODAL, EVENT_SEARCH_WITH_GEMINI_IN_FEATURE_LIBRARY_MODAL } from '../../tracking_constants';
import { HIDDEN_NAV_ITEM_CLASS, PANEL_TYPES } from '../../constants';
import { MODAL_ID, ITEMS_PER_RENDER_FRAME } from './constants';
import { rankSearchResults } from './search';
import FeatureLibraryItem from './feature_library_item.vue';
const SETTINGS_MENU_ID = 'settings_menu';
const trackingMixin = InternalEvents.mixin();
const MIN_SEARCH_QUERY_LENGTH = 2;
const ALL_CATEGORIES = 'all';
export default {
    name: 'FeatureLibraryModal',
    components: {
        GlModal,
        GlSearchBoxByType,
        GlButton,
        GlIcon,
        GlCollapse,
        GlEmptyState,
        GlLoadingIcon,
        ScrollScrim,
        FeatureLibraryItem
    },
    mixins: [
        trackingMixin
    ],
    modalId: MODAL_ID,
    DEFAULT_DEBOUNCE_AND_THROTTLE_MS,
    searchInputDescriptionId: 'feature-library-search-input-description',
    i18n: {
        geminiSearching: s__('FeatureLibrary|Searching with Gemini …'),
        searchInProgress: s__('FeatureLibrary|Searching for features …'),
        geminiEmptyState: s__("FeatureLibrary|Gemini couldn't find a matching feature. Try different keywords."),
        searchInputDescription: s__('FeatureLibrary|Press Enter or the down arrow key to move to the first search result.')
    },
    inject: {
        panelType: {
            default: ''
        },
        resourceId: {
            default: null
        },
        aiSearchAvailable: {
            default: false
        }
    },
    props: {
        supportsPins: {
            type: Boolean,
            required: false,
            default: false
        },
        sections: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        currentPinnedIds: {
            type: Array,
            required: false,
            default: ()=>[]
        }
    },
    emits: [
        'pin-toggle'
    ],
    data () {
        return {
            searchQuery: '',
            selectedCategory: ALL_CATEGORIES,
            collapsedSectionIds: [],
            searchResultIds: [],
            isSearching: false,
            latestQuery: null,
            renderLimit: ITEMS_PER_RENDER_FRAME,
            revealFrameId: null,
            geminiResultIds: [],
            isGeminiSearching: false,
            geminiSearched: false,
            geminiHidden: false,
            geminiErrorMessage: null
        };
    },
    computed: {
        trimmedQuery () {
            return this.searchQuery.trim();
        },
        hasSearchableQuery () {
            return this.trimmedQuery.length >= MIN_SEARCH_QUERY_LENGTH;
        },
        searchPlaceholder () {
            switch(this.panelType){
                case PANEL_TYPES.PROJECT:
                    return s__('FeatureLibrary|Search features in this project');
                case PANEL_TYPES.GROUP:
                    return s__('FeatureLibrary|Search features in this group');
                default:
                    return s__('FeatureLibrary|Search GitLab features');
            }
        },
        catalogSections () {
            return this.sections.map((section)=>({
                    ...section,
                    items: (section.items || []).filter((item)=>!item.link_classes?.includes(HIDDEN_NAV_ITEM_CLASS))
                })).filter((section)=>{
                // The settings menu currently remains in the super sidebar and should not appear within
                // the modal, nor have its items pinnable.
                if (section.id === SETTINGS_MENU_ID) {
                    return false;
                }
                return section.items.length > 0;
            });
        },
        catalog () {
            return this.catalogSections.flatMap((section)=>section.items.map((item)=>({
                        id: item.id,
                        title: item.title,
                        description: item.description,
                        icon: item.library_icon || item.icon,
                        tier: item.tier,
                        category: section.id,
                        link: item.link
                    })));
        },
        catalogById () {
            return Object.fromEntries(this.catalog.map((item)=>[
                    item.id,
                    item
                ]));
        },
        categoryOptions () {
            return [
                {
                    id: ALL_CATEGORIES,
                    title: s__('FeatureLibrary|All')
                },
                ...this.catalogSections.map((section)=>({
                        id: section.id,
                        title: section.title
                    }))
            ];
        },
        scopedCatalog () {
            if (this.selectedCategory === ALL_CATEGORIES) return this.catalog;
            return this.catalog.filter((item)=>item.category === this.selectedCategory);
        },
        filteredItems () {
            if (!this.trimmedQuery) return this.scopedCatalog;
            return rankSearchResults({
                catalog: this.scopedCatalog,
                query: this.trimmedQuery,
                synonymIds: this.searchResultIds
            });
        },
        // Sections with their items, honoring the progressive reveal limit.
        // Used when not searching, so each category renders under its own heading.
        //
        // Every section heading is always included so the section list is stable
        // regardless of the reveal budget; only the items within each section are
        // limited, and the budget is distributed across sections in order.
        groupedSections () {
            let remaining = this.renderLimit;
            return this.catalogSections.map((section)=>{
                const items = section.items.slice(0, Math.max(remaining, 0)).map((item)=>this.catalogById[item.id]).filter(Boolean);
                remaining -= items.length;
                return {
                    id: section.id,
                    title: section.title,
                    items
                };
            });
        },
        hasActiveCategoryFilter () {
            return this.selectedCategory !== ALL_CATEGORIES;
        },
        selectedCategoryLabel () {
            return this.categoryOptions.find((option)=>option.id === this.selectedCategory)?.title;
        },
        categoryStatusMessage () {
            if (this.isSearching || !this.hasActiveCategoryFilter) return '';
            const count = this.filteredItems.length;
            return sprintf(n__('FeatureLibrary|Showing %{count} feature in %{category}', 'FeatureLibrary|Showing %{count} features in %{category}', count), {
                count,
                category: this.selectedCategoryLabel
            });
        },
        showResultsGrid () {
            return Boolean(this.trimmedQuery) || this.hasActiveCategoryFilter;
        },
        showGroupedSections () {
            return !this.showResultsGrid;
        },
        showEmptyState () {
            return !this.isSearching && !this.isGeminiSearching && (this.hasSearchableQuery || this.hasActiveCategoryFilter) && this.filteredItems.length === 0 && !this.showGeminiEmptyState;
        },
        geminiItems () {
            if (!this.geminiResultIds.length) return [];
            const shownIds = new Set(this.filteredItems.map((item)=>item.id));
            const inScope = (item)=>this.selectedCategory === ALL_CATEGORIES || item.category === this.selectedCategory;
            return this.geminiResultIds.map((id)=>this.catalogById[id]).filter((item)=>item && inScope(item) && !shownIds.has(item.id));
        },
        showGeminiSection () {
            return (this.isGeminiSearching || this.geminiSearched || Boolean(this.geminiErrorMessage)) && !this.geminiHidden;
        },
        showGeminiHeader () {
            return (this.geminiSearched || Boolean(this.geminiErrorMessage)) && !this.geminiHidden;
        },
        showGeminiEmptyState () {
            return this.showGeminiSection && !this.isGeminiSearching && !this.geminiErrorMessage && this.geminiItems.length === 0;
        },
        showGeminiButton () {
            return this.aiSearchAvailable && this.resourceId && this.hasSearchableQuery && (!this.geminiSearched || this.geminiHidden) && !this.isGeminiSearching;
        },
        showFooter () {
            return this.showGeminiButton;
        },
        emptyStateTitle () {
            return s__('FeatureLibrary|No features match your search');
        },
        searchStatusMessage () {
            if (!this.trimmedQuery) {
                return '';
            }
            if (this.filteredItems.length > 0) {
                return n__('FeatureLibrary|%d feature found.', 'FeatureLibrary|%d features found.', this.filteredItems.length);
            }
            if (this.isSearching) {
                return this.$options.i18n.searchInProgress;
            }
            return this.emptyStateTitle;
        },
        showGeminiTopBorder () {
            // showEmptyState already requires !showGeminiEmptyState, so the two are
            // mutually exclusive: no need to check showEmptyState separately here.
            return !this.isGeminiSearching && !this.showGeminiEmptyState;
        },
        geminiStatusMessage () {
            if (!this.showGeminiSection) {
                return '';
            }
            if (this.isGeminiSearching) {
                return this.$options.i18n.geminiSearching;
            }
            if (this.geminiErrorMessage) {
                return this.geminiErrorMessage;
            }
            if (this.showGeminiEmptyState) {
                return this.$options.i18n.geminiEmptyState;
            }
            if (this.geminiItems.length > 0) {
                return n__('FeatureLibrary|Gemini found %d matching feature.', 'FeatureLibrary|Gemini found %d matching features.', this.geminiItems.length);
            }
            return '';
        }
    },
    beforeDestroy () {
        this.cancelReveal();
    },
    methods: {
        isPinned (itemId) {
            return this.currentPinnedIds.includes(itemId);
        },
        isSectionExpanded (sectionId) {
            return !this.collapsedSectionIds.includes(sectionId);
        },
        toggleSection (sectionId) {
            if (this.isSectionExpanded(sectionId)) {
                this.collapsedSectionIds.push(sectionId);
            } else {
                this.collapsedSectionIds = this.collapsedSectionIds.filter((id)=>id !== sectionId);
            }
        },
        isCategorySelected (categoryId) {
            return this.selectedCategory === categoryId;
        },
        selectCategory (categoryId) {
            const next = this.isCategorySelected(categoryId) ? ALL_CATEGORIES : categoryId;
            this.selectedCategory = next;
            this.trackEvent(EVENT_CLICK_CATEGORY_FILTER_IN_FEATURE_LIBRARY_MODAL, {
                label: next
            });
            this.$refs.searchBox?.focusInput?.();
        },
        onShown () {
            this.$refs.searchBox?.focusInput();
            this.revealRemainingItems();
            this.trackEvent(EVENT_OPEN_FEATURE_LIBRARY_MODAL);
        },
        focusFirstResult (event) {
            if (!this.trimmedQuery) return;
            // Move focus into the results instead of navigating away: focusing the
            // first result lets keyboard users continue from there
            // (e.g. Tab to its pin action, or Enter again to open it). When the
            // main grid is empty, fall through to the first Gemini suggestion; a
            // hidden Gemini section renders no refs, so this no-ops and both keys
            // keep their default behavior.
            const target = this.findResultComponent(this.$refs.searchResultItems, this.filteredItems[0]) || this.findResultComponent(this.$refs.geminiResultItems, this.geminiItems[0]);
            if (!target) return;
            event.preventDefault();
            target.focus();
        },
        // Match by id rather than taking refs[0]: Vue 2's v-for ref arrays
        // reflect registration order, which isn't guaranteed to track the
        // current (re-ranked) item order.
        findResultComponent (components, item) {
            if (!item) return null;
            return (components || []).find((component)=>component.item.id === item.id) || null;
        },
        revealRemainingItems () {
            if (this.renderLimit >= this.catalog.length) return;
            this.revealFrameId = window.requestAnimationFrame(()=>{
                this.renderLimit += ITEMS_PER_RENDER_FRAME;
                this.revealRemainingItems();
            });
        },
        cancelReveal () {
            if (this.revealFrameId) {
                window.cancelAnimationFrame(this.revealFrameId);
                this.revealFrameId = null;
            }
        },
        resetSearchState () {
            this.isSearching = false;
            this.latestQuery = null;
            this.searchResultIds = [];
        },
        resetGeminiState () {
            this.geminiResultIds = [];
            this.isGeminiSearching = false;
            this.geminiSearched = false;
            this.geminiHidden = false;
            this.geminiErrorMessage = null;
        },
        onSearchInput (value) {
            this.searchQuery = value;
            const query = value.trim();
            this.resetGeminiState();
            if (query) {
                this.fetchResults(query);
                this.trackEvent(EVENT_SEARCH_FEATURES_IN_FEATURE_LIBRARY_MODAL);
            } else {
                this.resetSearchState();
            }
        },
        onPinToggle (itemId, nextState, title) {
            this.$emit('pin-toggle', itemId, nextState, title);
            const event = nextState ? EVENT_PIN_ITEM_IN_FEATURE_LIBRARY_MODAL : EVENT_UNPIN_ITEM_IN_FEATURE_LIBRARY_MODAL;
            this.trackEvent(event, {
                label: itemId
            });
        },
        onNavigate (itemId) {
            this.trackEvent(EVENT_NAVIGATE_TO_FEATURE_FROM_FEATURE_LIBRARY_MODAL, {
                label: itemId
            });
        },
        onHidden () {
            this.resetSearchState();
            this.resetGeminiState();
            this.cancelReveal();
            this.searchQuery = '';
            this.selectedCategory = ALL_CATEGORIES;
            this.collapsedSectionIds = [];
            this.renderLimit = ITEMS_PER_RENDER_FRAME;
        },
        fetchResults (query) {
            if (query.length < MIN_SEARCH_QUERY_LENGTH) {
                this.resetSearchState();
                return;
            }
            this.latestQuery = query;
            this.isSearching = true;
            axios.get(onboardingFeatureLibrarySearchPath(), {
                params: {
                    query,
                    panel: this.panelType
                }
            }).then(({ data })=>{
                if (query !== this.latestQuery) return;
                this.applySearchResultIds(data.ids || []);
            }).catch((e)=>{
                if (query !== this.latestQuery) return;
                if (e.response?.status !== HTTP_STATUS_TOO_MANY_REQUESTS) {
                    Sentry.captureException(e, {
                        tags: {
                            feature_category: 'onboarding'
                        }
                    });
                }
                this.applySearchResultIds([]);
            }).finally(()=>{
                if (query !== this.latestQuery) return;
                this.isSearching = false;
            });
        },
        // A kept result can hold keyboard focus while the request is in flight; if
        // the response drops it, it unmounts and focus silently falls to <body>.
        applySearchResultIds (ids) {
            const focused = document.activeElement;
            const hadFocusedResult = this.isInsideResult(focused);
            this.searchResultIds = ids;
            if (!hadFocusedResult) return;
            this.$nextTick(()=>{
                if (!this.isInsideResult(focused)) this.$refs.searchBox?.focusInput?.();
            });
        },
        isInsideResult (element) {
            return (this.$refs.searchResultItems || []).some((item)=>item.$el.contains(element));
        },
        async searchWithGemini () {
            if (!this.resourceId || !this.aiSearchAvailable) return;
            const query = this.trimmedQuery;
            this.trackEvent(EVENT_SEARCH_WITH_GEMINI_IN_FEATURE_LIBRARY_MODAL);
            this.geminiHidden = false;
            this.isGeminiSearching = true;
            this.geminiErrorMessage = null;
            // The "Search with Gemini" button unmounts once searching starts, which would
            // otherwise silently drop keyboard focus to <body>. Return focus to the search
            // input, which remains mounted throughout.
            this.$refs.searchBox?.focusInput?.();
            try {
                const { data } = await axios.get(onboardingFeatureLibraryAiSearchPath(), {
                    params: {
                        query,
                        panel: this.panelType,
                        resource_id: this.resourceId
                    }
                });
                if (query !== this.trimmedQuery) return;
                this.geminiResultIds = data.ids || [];
                this.geminiSearched = true;
            } catch (e) {
                if (query !== this.trimmedQuery) return;
                const status = e.response?.status;
                if (status === HTTP_STATUS_TOO_MANY_REQUESTS) {
                    this.geminiErrorMessage = e.response?.data?.error || s__('FeatureLibrary|You have reached the search limit. Try again later.');
                } else {
                    // Includes 404: the button is gated on aiSearchAvailable, so this
                    // shouldn't normally occur; if it does (e.g. a stale flag/trial state),
                    // it's unexpected and worth reporting.
                    Sentry.captureException(e, {
                        tags: {
                            feature_category: 'onboarding'
                        }
                    });
                    this.geminiErrorMessage = s__('FeatureLibrary|Something went wrong searching with Gemini. Try again.');
                }
            } finally{
                if (query === this.trimmedQuery) {
                    this.isGeminiSearching = false;
                }
            }
        },
        hideGeminiSection () {
            this.geminiHidden = true;
            // Re-focusing as the Hide button unmounts along with the rest of the Gemini section
            this.$refs.searchBox?.focusInput?.();
        }
    }
};
